import { useTheme } from "next-themes";
import { Moon, Sun, Inbox } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export const ThemeToggle = () => {
  const { resolvedTheme, setTheme } = useTheme();
  const dark = resolvedTheme === "dark";
  return (
    <Button variant="ghost" size="icon" data-testid="theme-toggle-button" aria-label="Toggle theme" onClick={() => setTheme(dark ? "light" : "dark")}>
      {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </Button>
  );
};

export const PageHeader = ({ title, subtitle, actions, eyebrow }) => (
  <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-8 fade-up">
    <div>
      {eyebrow && <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-primary mb-2">{eyebrow}</p>}
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" data-testid="page-title">{title}</h1>
      {subtitle && <p className="text-sm text-muted-foreground mt-1.5 max-w-2xl">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

export const StatCard = ({ label, value, icon: Icon, hint, tone = "primary", testId, delay = 0 }) => {
  const tones = {
    primary: "bg-primary/10 text-primary",
    amber: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
    emerald: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    sky: "bg-sky-500/10 text-sky-600 dark:text-sky-400",
    rose: "bg-rose-500/10 text-rose-600 dark:text-rose-400",
  };
  return (
    <div className="bg-card rounded-xl border p-5 flex items-start justify-between fade-up hover:shadow-md transition-shadow" style={{ animationDelay: `${delay}ms` }}>
      <div className="min-w-0">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{label}</p>
        <p className="font-display text-2xl sm:text-3xl font-bold mt-2 truncate" data-testid={testId}>{value}</p>
        {hint && <p className="text-xs text-muted-foreground mt-1.5">{hint}</p>}
      </div>
      {Icon && <div className={cn("h-10 w-10 rounded-lg flex items-center justify-center shrink-0", tones[tone])}><Icon className="h-5 w-5" /></div>}
    </div>
  );
};

const PILL = {
  verified: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20",
  approved: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20",
  active: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20",
  won: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20",
  published: "bg-primary/10 text-primary border-primary/20",
  pending: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20",
  scheduled: "bg-sky-500/10 text-sky-700 dark:text-sky-400 border-sky-500/20",
  rejected: "bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/20",
  suspended: "bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/20",
  lost: "bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/20",
  cancelled: "bg-muted text-muted-foreground border-border",
  expired: "bg-muted text-muted-foreground border-border",
};

export const StatusPill = ({ status, testId }) => (
  <span data-testid={testId} className={cn("inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border capitalize whitespace-nowrap", PILL[status] || "bg-secondary text-secondary-foreground border-border")}>
    {String(status || "—").replace("_", " ")}
  </span>
);

export const EmptyState = ({ title, text, action, testId }) => (
  <div className="rounded-xl border border-dashed p-10 text-center bg-card" data-testid={testId}>
    <Inbox className="h-8 w-8 mx-auto text-muted-foreground" />
    <p className="font-display font-semibold mt-3">{title}</p>
    {text && <p className="text-sm text-muted-foreground mt-1">{text}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export const Panel = ({ title, actions, children, className }) => (
  <div className={cn("bg-card rounded-xl border fade-up", className)}>
    {title && (
      <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b">
        <h3 className="font-display font-semibold">{title}</h3>
        {actions}
      </div>
    )}
    <div className="p-5 sm:p-6">{children}</div>
  </div>
);
